import type { Appointment, HoursRange } from './types';

export const CALENDAR_TIME_ZONE = 'America/La_Paz';

export const isCanceledOrNoShowAppointment = (appointment: Appointment) =>
  !!appointment.isNoShow ||
  !!appointment.canceledAt ||
  String(appointment.status || '').toLowerCase() === 'canceled';

type ZonedParts = {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
};

const getZonedParts = (date: Date, timeZone: string): ZonedParts => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }).formatToParts(date);

  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '0';

  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour: Number(get('hour')),
    minute: Number(get('minute')),
    second: Number(get('second'))
  };
};

export const getZonedDate = (date: Date, timeZone = CALENDAR_TIME_ZONE) => {
  const parts = getZonedParts(date, timeZone);
  return new Date(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
};

const makeUtcDateFromZonedParts = (parts: ZonedParts, timeZone = CALENDAR_TIME_ZONE) => {
  const targetUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second || 0);
  let guessUtc = targetUtc;

  for (let i = 0; i < 2; i++) {
    const zonedGuess = getZonedParts(new Date(guessUtc), timeZone);
    const zonedGuessAsUtc = Date.UTC(
      zonedGuess.year,
      zonedGuess.month - 1,
      zonedGuess.day,
      zonedGuess.hour,
      zonedGuess.minute,
      zonedGuess.second || 0
    );
    const diffMs = targetUtc - zonedGuessAsUtc;
    if (diffMs === 0) break;
    guessUtc += diffMs;
  }

  return new Date(guessUtc);
};

export const makeDateInTimeZone = (day: Date, hour: number, minute: number, timeZone = CALENDAR_TIME_ZONE) => {
  const dayParts = getZonedParts(day, timeZone);
  return makeUtcDateFromZonedParts({ ...dayParts, hour, minute, second: 0 }, timeZone);
};

export const UNASSIGNED_DOCTOR_COLOR = '#cbd5e1';
export const CLINIC_ROOMS = [1, 2, 3, 4, 5] as const;

type RoomDoctor = {
  id: number;
  firstName?: string;
  lastName?: string;
  defaultRoom?: number | null;
};

type RoomPatient = {
  dateOfBirth?: string | null;
};

type AppointmentRoomInput = Pick<Appointment, 'id' | 'startTime' | 'endTime' | 'room' | 'isAllDay'>;

const isClinicRoom = (room: number | null | undefined): room is number =>
  typeof room === 'number' && CLINIC_ROOMS.includes(room as typeof CLINIC_ROOMS[number]);

const appendRoom = (rooms: number[], room: number | null | undefined) => {
  if (isClinicRoom(room) && !rooms.includes(room)) rooms.push(room);
};

export const buildAutoRoomOrder = (doctor?: RoomDoctor | null, patient?: RoomPatient | null, now = new Date()) => {
  const preferredOrder: number[] = [];
  appendRoom(preferredOrder, doctor?.defaultRoom);

  const doctorName = `${doctor?.firstName || ''} ${doctor?.lastName || ''}`.toLowerCase();
  const isNathy = /\b(nathy|nathaly)\b/.test(doctorName);
  let isYoung = false;
  if (patient?.dateOfBirth) {
    const dob = new Date(patient.dateOfBirth);
    const ageDiffMs = now.getTime() - dob.getTime();
    const ageDate = new Date(ageDiffMs);
    isYoung = Math.abs(ageDate.getUTCFullYear() - 1970) < 15;
  }

  if (isNathy) appendRoom(preferredOrder, 4);
  if (isYoung) appendRoom(preferredOrder, 2);
  [3, 4, 5, 1, 2].forEach(room => appendRoom(preferredOrder, room));
  return preferredOrder;
};

export const getAutoAssignedRoom = ({
  selectedRoom,
  startDateTime,
  endDateTime,
  appointments,
  doctor,
  patient,
  editingAppointmentId,
  now
}: {
  selectedRoom: string;
  startDateTime: Date | null;
  endDateTime: Date | null;
  appointments: AppointmentRoomInput[];
  doctor?: RoomDoctor | null;
  patient?: RoomPatient | null;
  editingAppointmentId?: number | null;
  now?: Date;
}) => {
  if (selectedRoom || !startDateTime || !endDateTime) return selectedRoom;

  for (const room of buildAutoRoomOrder(doctor, patient, now)) {
    const busy = appointments.some(appointment => {
      if (editingAppointmentId != null && appointment.id === editingAppointmentId) return false;
      if (!appointment.startTime || !appointment.endTime) return false;
      const appointmentStart = new Date(appointment.startTime);
      const appointmentEnd = new Date(appointment.endTime);
      return startDateTime < appointmentEnd && endDateTime > appointmentStart && appointment.room === room;
    });
    if (!busy) return room.toString();
  }

  return '';
};

// Deterministic pastel color generator based on numeric id (e.g., patientId)
export const getFallbackColor = (id: number | null | undefined) => {
  const safeId = id || 0;
  const hues = [210, 260, 180, 330, 30, 120];
  const hue = hues[safeId % hues.length];
  const saturation = 55 + (safeId % 20);
  const lightness = 65;
  return `hsl(${hue} ${saturation}% ${lightness}%)`;
};

type ColorDoctor = { id: number; color?: string | null };

const resolveDoctor = (
  id: number | null | undefined,
  embedded: { color?: string | null } | null | undefined,
  doctors: ColorDoctor[]
) => (id ? doctors.find(d => d.id === id) : undefined) || (embedded ? { id: id ?? -1, color: embedded.color } : undefined);

// Block fill used for an appointment: prospect yellow, else doctor color, else stored color, else fallback.
export const getAppointmentBlockColor = (app: Appointment, doctors: ColorDoctor[]) => {
  if (app.isProspect) return '#fde68a';
  const doc = resolveDoctor(app.doctorId, app.doctor, doctors);
  return (doc?.color as string | undefined)
    || (doc ? app.color : undefined)
    || (!app.doctorId ? UNASSIGNED_DOCTOR_COLOR : undefined)
    || getFallbackColor(app.patientId || 0);
};

export const getAppointmentSecondColor = (app: Appointment, doctors: ColorDoctor[]) =>
  (resolveDoctor(app.secondDoctorId, app.secondDoctor, doctors)?.color as string | undefined) || undefined;

// Picks dark or white text for a hex background (YIQ contrast).
export const getReadableTextColor = (background: string) => {
  const c = background.startsWith('#') ? background.substring(1) : background;
  if (c.length !== 6) return '#fff';
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return '#fff';
  return ((r * 299) + (g * 587) + (b * 114)) / 1000 >= 160 ? '#1f2937' : '#fff';
};

export const getWeekDays = (date: Date) => {
  const week: Date[] = [];
  const startOfWeek = new Date(date);
  const day = startOfWeek.getDay();
  const diff = day === 0 ? -6 : 1 - day; // shift to Monday
  startOfWeek.setDate(startOfWeek.getDate() + diff);
  for (let i = 0; i < 6; i++) {
    const d = new Date(startOfWeek);
    d.setDate(startOfWeek.getDate() + i);
    week.push(d);
  }
  return week;
};

export const startOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

export const endOfDay = (date: Date) => {
  const d = new Date(date);
  d.setHours(23, 59, 59, 999);
  return d;
};

export const normalizeDateRange = (start: Date, end: Date) => {
  const startDay = startOfDay(start);
  const endDay = endOfDay(end);

  if (startDay.getTime() <= endDay.getTime()) {
    return { start: startDay, end: endDay };
  }

  return { start: startOfDay(end), end: endOfDay(start) };
};

export const getInclusiveDateRange = (start: Date, end: Date) => {
  const range = normalizeDateRange(start, end);
  const days: Date[] = [];
  const cursor = startOfDay(range.start);
  const last = startOfDay(range.end);

  while (cursor.getTime() <= last.getTime()) {
    days.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return days;
};

export const isLongDateRange = (start: Date, end: Date, maxDays = 7) =>
  getInclusiveDateRange(start, end).length > maxDays;

export const formatTime = (d: Date) => new Intl.DateTimeFormat('en-US', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: CALENDAR_TIME_ZONE
}).format(d);

export const getAppointmentPosition = (
  appointment: Appointment,
  HOUR_HEIGHT: number,
  hoursRangeForWeek: HoursRange
) => {
  if (!appointment.startTime || !appointment.endTime) {
    return { top: '0px', height: '0px', hidden: true } as any;
  }
  const startTime = getZonedDate(new Date(appointment.startTime));
  const endTime = getZonedDate(new Date(appointment.endTime));
  let startHour = startTime.getHours() + startTime.getMinutes() / 60;
  let endHour = endTime.getHours() + endTime.getMinutes() / 60;
  if (endHour <= hoursRangeForWeek.start || startHour >= hoursRangeForWeek.end) {
    return { top: '0px', height: '0px', hidden: true } as any;
  }
  startHour = Math.max(startHour, hoursRangeForWeek.start);
  endHour = Math.min(endHour, hoursRangeForWeek.end);
  const duration = Math.max(0.25, endHour - startHour);
  const topOffset = (startHour - hoursRangeForWeek.start) * HOUR_HEIGHT;
  const height = duration * HOUR_HEIGHT;
  return { top: `${topOffset}px`, height: `${Math.max(2, height - 2)}px` };
};

export type LaidOutAppointment = Appointment & { _col: number; _cols: number };

export const layoutAppointments = (apps: Appointment[]): LaidOutAppointment[] => {
  if (apps.length === 0) return [];

  // 1. Sort by start time, then by duration (longer first) for better packing
  const sorted = [...apps].sort((a, b) => {
    const startA = new Date(a.startTime!).getTime();
    const startB = new Date(b.startTime!).getTime();
    if (startA !== startB) return startA - startB;
    const durA = new Date(a.endTime!).getTime() - startA;
    const durB = new Date(b.endTime!).getTime() - startB;
    return durB - durA;
  });

  const result: LaidOutAppointment[] = sorted.map(a => ({ ...a, _col: 0, _cols: 1 }));

  // 2. Assign columns
  // Iterate through events. For each event, place it in the first column where it doesn't collide with existing events in that column.
  const columns: LaidOutAppointment[][] = [];
  
  for (const app of result) {
    let placed = false;
    for (let i = 0; i < columns.length; i++) {
      const col = columns[i];
      const lastInCol = col[col.length - 1];
      const lastEnd = new Date(lastInCol.endTime!).getTime();
      const currentStart = new Date(app.startTime!).getTime();
      
      if (currentStart >= lastEnd) {
        col.push(app);
        app._col = i;
        placed = true;
        break;
      }
    }
    
    if (!placed) {
      columns.push([app]);
      app._col = columns.length - 1;
    }
  }

  // 3. Compute _cols per appointment based on direct time overlaps.
  // Using the cluster max-col approach was too broad: appointments far apart in time
  // could end up in the same cluster (via a long chain) and all get the same narrow
  // width even though they don't overlap with anything. Instead, each appointment's
  // column count = 1 + max(_col of all appointments it directly overlaps with).
  for (const app of result) {
    const appStart = new Date(app.startTime!).getTime();
    const appEnd = new Date(app.endTime!).getTime();
    let maxCol = app._col;
    for (const other of result) {
      if (other === app) continue;
      const otherStart = new Date(other.startTime!).getTime();
      const otherEnd = new Date(other.endTime!).getTime();
      if (otherStart < appEnd && otherEnd > appStart) {
        maxCol = Math.max(maxCol, other._col);
      }
    }
    app._cols = maxCol + 1;
  }

  return result;
};

export const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export const startOfMonth = (d: Date) => {
  const x = new Date(d);
  x.setDate(1);
  x.setHours(0, 0, 0, 0);
  return x;
};

export const endOfMonth = (d: Date) => {
  const x = new Date(d);
  x.setMonth(x.getMonth() + 1, 0);
  x.setHours(23, 59, 59, 999);
  return x;
};

export const getMonthGrid = (anchor: Date) => {
  const start = startOfMonth(anchor);
  const day = start.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  start.setDate(start.getDate() + diff);
  const weeks: Date[][] = [];
  let cursor = new Date(start);
  for (let w = 0; w < 6; w++) {
    const week: Date[] = [];
    for (let i = 0; i < 7; i++) {
      week.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(week);
    if (cursor > endOfMonth(anchor) && cursor.getDay() === 1) break;
  }
  return weeks;
};
